import { BaseModal, ModalOptions, ModalColor, ModalResultType } from "../../../../../../core/modal/index";
import { Template } from "../../../../../../core/template/index";
import ConfirmModal from "../../../../../../core/modals/confirm/index";
import { SelectOptionData } from "../../../../../../core/view/data-bind/types/selectbox-base";
import { ClientAction } from "../../../../../synchronizer/index";
import { FortumApp } from "../../../../../fortumApp";
import DeconCleansingTypeUsage, { CleansingInstanceLike, CleansingTypeUsageLike, CleansingInstance } from "../../../../../db/models/decontamination/cleansingTypeusage";
import { CleansingType } from "../../../../../db/models/decontamination/index";
import ListView from "../../../../../../core/component/listView/index";
import CleansingTypeUsageListItem, { CleansingTypeUsageListItemData } from "./cleansing-type-usage-list-item/index";
import { ViewEvent } from "../../../../../../core/view/index";
import { ViewDataBindEvents, DataPropertyChangedEventArgs } from "../../../../../../core/view/data-bind/index";
import { OrderItem } from "../../../../../db/models/decontamination/orderItem";


export default class OrderItemEditCleansingInstanceModal extends BaseModal
{
    public template = Template.lazy('/app/views/decontamination/orderItem/modals/edit-cleansing-instance/index.html');
    public data: OrderItemEditCleansingInstanceModalData;
    public app: FortumApp;
    private saveButton: HTMLButtonElement;
    private cancelButton: HTMLButtonElement;
    private cleansingTypeList: ListView;
    private listViewContainer: HTMLElement;
    private availableCleansingTypes: CleansingType[];
    private addButton: HTMLButtonElement;
    private removeButton: HTMLButtonElement;
    private cancelRemoveButton: HTMLButtonElement;

    private footer: HTMLElement;
    private confirmation: HTMLElement;

    private cleansingTypeUsageItemsData: CleansingTypeUsageListItemData[];

    constructor(data?: OrderItemEditCleansingInstanceModalData)
    {
        super(data);
    }

    public get defaults(): ModalOptions
    {
        return {
            title: this.pgettext('decontamination.OrderItem', 'Edit cleansing instance')
        }
    }

    public beforeRender(): PromiseLike<void>
    {
        this.data.cleansingTypeIdsToAdd = [];
        this.data.cleansingTypeUsageIdsToRemove = [];
        this.on(ViewDataBindEvents.DATA_CHANGED, this.onDataChanged);

        this.data.title = this.title;
        return super.beforeRender().then(() =>
        {
            return <PromiseLike<CleansingType[]>>this.app.db.DeconCleansingType.toArray(); 
        }).then((availableCleansingTypes) =>
        {
            this.availableCleansingTypes = availableCleansingTypes.filter(o => !o.is_legacy);
            return <PromiseLike<OrderItem>>this.app.db.DeconOrderItem.where('id').equals(this.data.item_id).first();
        }).then((item) => {
            this.data.item = item;
            let options: SelectOptionData[] = [
                {content: '-----', value: ''},
            ];
            
            options = options.concat(this.availableCleansingTypes.map(o => <SelectOptionData>{
                content: o.name,
                value: o.id,
            }));

            this.data.availableCleansingTypes = options;
            this.data.cleansingTypeToAdd = this.data.availableCleansingTypes[options.length > 1 ? 1 : 0];
            this.cleansingTypeUsageItemsData = this.data.usages.map(o => <CleansingTypeUsageListItemData>{
                type: o.type,
                instance_id: this.data.id,
                cleansing_type_usage_id: o.id,
            });

            this.cleansingTypeList = new ListView(
                {
                    initialItems: this.cleansingTypeUsageItemsData,
                }, 
                CleansingTypeUsageListItem
            );

            this.cleansingTypeList.on('removeCleansingTypeUsage', (sender, item: CleansingTypeUsageListItem) =>
            {
                if (item.data.cleansing_type_usage_id != null)
                {
                    this.data.cleansingTypeUsageIdsToRemove.push(item.data.cleansing_type_usage_id);
                }
                
                this.cleansingTypeList.removeItem(item);
            });
            
            return this.app.renderPartialView(this.cleansingTypeList);
        });

    }
    
    private onDataChanged = (sender, args: DataPropertyChangedEventArgs) =>
    {
        if (args.propertyName == 'cleansingTypeToAdd')
        {
            this.addButton.disabled = this.data.cleansingTypeToAdd.value == '';
        }
    };

    public afterRender()
    {
        return super.afterRender().then(() =>
        {
            this.listViewContainer.appendChild(this.cleansingTypeList.element);
            this.updateSaveButtonDisabledState();
        });
    }

    private onAddButtonClick(e: MouseEvent, sender: HTMLButtonElement)
    {
        let data = <CleansingTypeUsageListItemData>{
            instance_id: this.data.id,
            type: this.availableCleansingTypes.find(o => o.id == this.data.cleansingTypeToAdd.value),
            cleansing_type_usage_id: null,
        };
        this.cleansingTypeList.addItem(data);
        this.updateSaveButtonDisabledState();
    }

    private updateSaveButtonDisabledState()
    {  
        if (this.data.isNew)
        {
            this.removeButton.style.display = 'none';
        }
        this.saveButton.disabled = this.data.id == null && this.cleansingTypeList.items.length == 0;
    }

    private onCancelButtonClick(e: MouseEvent, sender: HTMLButtonElement)
    {
        this.helpers.setButtonEnabled(sender, false); 
        this.close(ModalResultType.CANCEL);
    }

    private get title()
    {
        return this.pgettext('decontamination.OrderItem', 'Edit cleansing instance');
    }

    private get toastText()
    {
        return this.app.pgettext('decontamination.OrderItem', 'Successfully updated cleansing instance!');
    }

    private onSaveButtonClick(e: MouseEvent, sender: HTMLButtonElement)
    {
        this.helpers.setButtonEnabled(this.saveButton, false); 
        this.helpers.setButtonEnabled(this.cancelButton, false); 

        this.app.db.DeconCleansingInstance.put({
            item_id: this.data.item_id,
            is_offline: 1,
            ...(this.data.id ? {id: this.data.id} : {}),
        }).then((instanceId) => {
            // Delete existing
            return this.app.db.DeconCleansingTypeUsage.where('instance_id').equals(instanceId).delete().then(() => {
                // Add all 
                return this.app.db.DeconCleansingTypeUsage.bulkAdd((this.cleansingTypeList.items as CleansingTypeUsageListItem[]).map(type => ({
                    instance_id: instanceId,
                    type_id: type.data.type.id,
                    is_offline: 1,
                })));
            }).then(() => {
                return OrderItem.trySetStarted(this.app.db, this.data.item.order_id, this.data.item_id);
            });
        }).then(() => {
            return this.app.synchronizer.createMessageAndPull({
                action: ClientAction.DECONTAMINATION.UPDATE_ORDER_ITEM_CLEANSING_INSTANCE,
                data: {
                    order_item_id: this.data.item_id,
                    cleansing_instance_id: this.data.id,
                    cleansing_type_ids: (this.cleansingTypeList.items as CleansingTypeUsageListItem[]).map(o => o.data.type.id)
                }
            });
        }).then(() =>
        {
            this.close(ModalResultType.OK).then(() =>
            {
                this.app.toasts.show(this.toastText);
            })
        });
    }

    private onRemoveButtonClick(e: MouseEvent, sender: HTMLButtonElement)
    {   
        this.helpers.setButtonEnabled(this.removeButton, false);
        this.helpers.setButtonEnabled(this.saveButton, false);
        this.helpers.setButtonEnabled(this.cancelButton, false);
        this.footer.style.display = 'none';
        this.confirmation.style.display = 'block';
    }

    private onCancelRemoveButtonClick(e: MouseEvent, sender: HTMLButtonElement)
    {
        this.helpers.setButtonEnabled(this.removeButton, true);
        this.helpers.setButtonEnabled(this.saveButton, true);
        this.helpers.setButtonEnabled(this.cancelButton, true);
        this.footer.style.display = 'flex';
        this.confirmation.style.display = 'none';
    }

    private onConfirmRemoveButtonClick(e: MouseEvent, sender: HTMLButtonElement)
    {
        this.helpers.setButtonEnabled(sender, false);
        this.helpers.setButtonEnabled(this.cancelRemoveButton, false);
        return Promise.all([
            this.app.db.DeconCleansingTypeUsage.where('instance_id').equals(this.data.id).delete(),
            this.app.db.DeconCleansingInstance.where('id').equals(this.data.id).delete(),
        ]).then(() => {
            return this.app.synchronizer.push({
                action: ClientAction.DECONTAMINATION.REMOVE_ORDER_ITEM_CLEANSING_INSTANCE,
                data: {
                    order_item_id: this.data.item_id,
                    cleansing_instance_id: this.data.id,
                }
            }, true, false).then(() =>
            {
                this.app.refreshCurrentView({tab: 'cleansing'}).then(() =>
                {
                    this.app.toasts.show(this.app.pgettext(
                        'decontamination.OrderItem',
                        'Successfully removed a cleansing instance!'
                    ));
                });
                this.close();
            });
        })
    }
}

export interface OrderItemEditCleansingInstanceModalData extends CleansingInstanceLike, ModalOptions
{
    isNew?: boolean;
    cleansingTypeIdsToAdd?: (number | string)[];
    cleansingTypeUsageIdsToRemove?: (number | string)[];
    availableCleansingTypes?: SelectOptionData[];
    cleansingTypeToAdd?: SelectOptionData;
}